/**
 * The splash's living meadow (Sept 2026): receipts keep rising from the
 * friends' table, and fireflies twinkle in the wheat. Drawn on two canvases,
 * one behind the painted receipts and one in front, positioned against the
 * painting (meadow.webp) wherever the screen crops it.
 */

// Where things are in the painting, as fractions of the image.
const IMG_ASPECT = 1152 / 2496;
const TABLE = { x: 0.52, y: 0.66 };
const MEADOW = { x0: 0.04, x1: 0.96, y0: 0.5, y1: 0.95 };

type Paper = { x: number; y: number; vy: number; vx: number; s: number; r: number; vr: number; ph: number };
type Fly = { fx: number; fy: number; vx: number; vy: number; ph: number; sp: number; r: number };

const rand = (a: number, b: number) => a + Math.random() * (b - a);

/** Starts the animation; returns a function that stops it. */
export function startMeadow(root: HTMLElement): () => void {
  const img = root.querySelector(".sa-img") as HTMLElement;
  const back = root.querySelector(".sa-back") as HTMLCanvasElement;
  const front = root.querySelector(".sa-front") as HTMLCanvasElement;
  const still = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // Screen geometry, refreshed on resize: the painting's rect (object-fit:
  // cover, anchored to the bottom of its box) and where its box fades out.
  let W = 0, H = 0, u = 1, ix = 0, iy = 0, iw = 0, ih = 0, boxTop = 0, fadeY = 0;
  const at = (fx: number, fy: number) => [ix + fx * iw, iy + fy * ih];
  const resize = () => {
    const r = root.getBoundingClientRect(), b = img.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    W = r.width; H = r.height; u = b.width / 390;
    const bx = b.left - r.left, by = b.top - r.top;
    if (b.width / b.height > IMG_ASPECT) { iw = b.width; ih = b.width / IMG_ASPECT; ix = bx; iy = by + b.height - ih; }
    else { ih = b.height; iw = b.height * IMG_ASPECT; iy = by; ix = bx + (b.width - iw) / 2; }
    boxTop = by; fadeY = by + b.height * 0.84;
    for (const c of [back, front]) {
      c.width = W * dpr; c.height = H * dpr;
      c.getContext("2d")!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  };
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(root);

  const spawn = (p: Partial<Paper>, near: boolean, anywhere: boolean): Paper => {
    const [sx, sy] = at(TABLE.x, TABLE.y);
    p.x = sx + rand(-14, 14) * u;
    p.y = anywhere ? rand(boxTop, sy) : sy + rand(-6, 8) * u;
    p.vy = rand(16, 30) * u * (near ? 1.35 : 1);
    p.vx = rand(-4, 4) * u;
    p.s = rand(7, 11) * u * (near ? 1.5 : 1);
    p.r = rand(0, Math.PI * 2); p.vr = rand(-1.2, 1.2); p.ph = rand(0, 6.28);
    return p as Paper;
  };
  const layers: [HTMLCanvasElement, Paper[], boolean][] = [
    [back, Array.from({ length: 12 }, () => spawn({}, false, true)), false],
    [front, Array.from({ length: 4 }, () => spawn({}, true, true)), true],
  ];
  const flies: Fly[] = Array.from({ length: 12 }, () => ({
    fx: rand(MEADOW.x0, MEADOW.x1), fy: rand(MEADOW.y0, MEADOW.y1),
    vx: rand(-0.012, 0.012), vy: rand(-0.01, 0.006), ph: rand(0, 6.28), sp: rand(0.8, 1.8), r: rand(2.2, 4.2),
  }));

  const paper = (g: CanvasRenderingContext2D, p: Paper, a: number) => {
    g.save(); g.translate(p.x, p.y); g.rotate(p.r); g.scale(1, Math.cos(p.ph) * 0.45 + 0.55);
    g.globalAlpha = a; g.fillStyle = "#FBF6EC"; g.shadowColor = "rgba(90,60,30,.25)"; g.shadowBlur = 3 * u;
    g.fillRect(-p.s * 0.5, -p.s * 0.7, p.s, p.s * 1.4); g.shadowBlur = 0;
    g.fillStyle = "rgba(90,80,70,.45)";
    for (let j = 0; j < 4; j++) g.fillRect(-p.s * 0.32, -p.s * 0.45 + j * p.s * 0.26, p.s * (j % 2 ? 0.5 : 0.64), Math.max(0.6, p.s * 0.06));
    g.restore();
  };

  let raf = 0, last = performance.now();
  const frame = (now: number) => {
    const dt = Math.min(0.05, (now - last) / 1000), t = now / 1000;
    last = now;
    const sy = at(TABLE.x, TABLE.y)[1];
    for (const [c, ps, near] of layers) {
      const g = c.getContext("2d")!;
      g.clearRect(0, 0, W, H);
      for (const p of ps) {
        p.y -= p.vy * dt; p.x += (p.vx + Math.sin(t * 0.9 + p.ph) * 9 * u) * dt;
        p.r += p.vr * dt; p.ph += dt * 2.2;
        if (p.y < boxTop - 20 * u) spawn(p, near, false);
        const life = (sy - p.y) / Math.max(1, sy - boxTop);
        const a = Math.min(1, life * 5) * Math.min(1, (p.y - boxTop) / (60 * u)) * (near ? 0.95 : 0.8);
        if (a > 0) paper(g, p, a);
      }
    }
    const g = front.getContext("2d")!;
    for (const f of flies) {
      f.fx += f.vx * dt + Math.sin(t * 0.7 + f.ph) * 0.0006; f.fy += f.vy * dt + Math.cos(t * 0.5 + f.ph) * 0.0005;
      if (f.fx < MEADOW.x0 || f.fx > MEADOW.x1) f.vx *= -1;
      if (f.fy < MEADOW.y0 || f.fy > MEADOW.y1) f.vy *= -1;
      const [cx, cy] = at(f.fx, f.fy);
      const fade = Math.max(0, Math.min(1, (fadeY - cy) / (40 * u)));
      const tw = Math.max(0, Math.sin(t * f.sp + f.ph)) ** 2 * fade;
      if (tw <= 0) continue;
      const core = f.r * u, halo = core * 5;
      const grd = g.createRadialGradient(cx, cy, 0, cx, cy, halo);
      grd.addColorStop(0, `rgba(255,246,190,${0.95 * tw})`);
      grd.addColorStop(0.18, `rgba(255,222,120,${0.75 * tw})`);
      grd.addColorStop(1, "rgba(255,200,80,0)");
      g.fillStyle = grd; g.beginPath(); g.arc(cx, cy, halo, 0, Math.PI * 2); g.fill();
    }
    if (!still) raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return () => { cancelAnimationFrame(raf); ro.disconnect(); };
}
